/**
 * 系统设置 Tab — 组合 LLM 配置 / 生成设置 / 截断设置 / 认证设置
 */
import React from 'react';
import { SystemModelConfig } from '../types';
import LLMConfigSection from './LLMConfigSection';
import GenerationSettingsSection from './GenerationSettingsSection';
import TruncationSettingsSection from './TruncationSettingsSection';
import PosterSlogansSection from './PosterSlogansSection';

interface SystemSettingsTabProps {
  modelConfig: SystemModelConfig;
  setModelConfig: (cfg: SystemModelConfig) => void;
  configSaving: boolean;
  configMsg: { type: 'success' | 'error'; text: string } | null;
  handleSaveConfig: () => void;
  handleSaveCasdoor: () => void;
  availableModels: string[];
  fetchingModels: boolean;
  fetchModelsMsg: string | null;
  showModelDropdown: boolean;
  setShowModelDropdown: (v: boolean) => void;
  filteredModels: string[];
  handleFetchModels: () => void;
  includeAllModels: boolean;
  setIncludeAllModels: (v: boolean) => void;
  modelSearchRef: React.RefObject<HTMLDivElement | null>;
  modelInputRef: React.RefObject<HTMLInputElement | null>;
  posterSlogansText: string;
  setPosterSlogansText: (v: string) => void;
  handleSavePosterSlogans: () => void;
  referralRatio: string;
  setReferralRatio: (v: string) => void;
  handleSaveReferralRatio: () => void;
}

export default function SystemSettingsTab(props: SystemSettingsTabProps) {
  const common = {
    modelConfig: props.modelConfig,
    setModelConfig: props.setModelConfig,
    configSaving: props.configSaving,
    handleSaveConfig: props.handleSaveConfig,
  };

  return (
    <>
      {/* 统一用户认证开关 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">统一用户认证</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-700">启用统一用户认证（Casdoor OAuth）</p>
            <p className="text-xs text-gray-400 mt-0.5">
              开启后用户可通过 Casdoor 统一认证登录，关闭后仅支持账号密码登录
            </p>
          </div>
          <button
            type="button"
            onClick={() => props.setModelConfig({
              ...props.modelConfig,
              casdoor_enabled: !props.modelConfig.casdoor_enabled,
            })}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              props.modelConfig.casdoor_enabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                props.modelConfig.casdoor_enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        <div className="mt-4">
          <button
            onClick={props.handleSaveCasdoor}
            disabled={props.configSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60 transition-colors"
          >
            {props.configSaving ? '保存中...' : '保存统一认证配置'}
          </button>
        </div>
      </div>

      <LLMConfigSection
        {...common}
        configMsg={props.configMsg}
        availableModels={props.availableModels}
        fetchingModels={props.fetchingModels}
        fetchModelsMsg={props.fetchModelsMsg}
        showModelDropdown={props.showModelDropdown}
        setShowModelDropdown={props.setShowModelDropdown}
        filteredModels={props.filteredModels}
        handleFetchModels={props.handleFetchModels}
        includeAllModels={props.includeAllModels}
        setIncludeAllModels={props.setIncludeAllModels}
        modelSearchRef={props.modelSearchRef}
        modelInputRef={props.modelInputRef}
      />

      <hr className="my-6 border-gray-200" />

      <GenerationSettingsSection {...common} />

      <hr className="my-6 border-gray-200" />

      <TruncationSettingsSection {...common} />

      <hr className="my-6 border-gray-200" />

      <PosterSlogansSection
        text={props.posterSlogansText}
        onChange={props.setPosterSlogansText}
        onSave={props.handleSavePosterSlogans}
        saving={props.configSaving}
        msg={props.configMsg}
      />

      <hr className="my-6 border-gray-200" />

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">邀请积分奖励比例</h3>
        <p className="text-xs text-gray-400 mb-2">
          被邀请好友消耗积分时，邀请人按其消耗量的该比例获得奖励（如 0.15 表示 15%）。取值范围 0~1。
        </p>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={props.referralRatio}
            onChange={(e) => props.setReferralRatio(e.target.value)}
            className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={props.handleSaveReferralRatio}
            disabled={props.configSaving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-60 transition-colors"
          >
            {props.configSaving ? '保存中...' : '保存比例'}
          </button>
          {props.configMsg && (
            <span className={`text-xs ${props.configMsg.type === 'success' ? 'text-green-600' : 'text-red-500'}`}>
              {props.configMsg.text}
            </span>
          )}
        </div>
      </div>
    </>
  );
}
